import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  IconButton,
  Alert,
  Stack,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Api as ApiIcon,
  Code as CodeIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as TimeIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Block as BlockIcon,
  PlayCircleOutline as SandboxIcon,
  PersonAdd as ClaimIcon,
  CheckCircleOutline as ApproveIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import type { RootState } from "../../../store/Index";
import type { EndpointRequest, HttpMethod, Priority, RequestStatus, AssignedUser } from "../types";
import { isBackendDev, isAdmin } from "../utils/permissions";
import {
  ModernStatusBadge,
  ModernMethodChip,
  ModernPriorityChip,
} from "./ModernUiComponents";
import { CodeSnippetDialog } from "./CodeSnippetDialog";
import { JsonEditorInput } from "./JsonEditorInput";
import { ApiTesterSandbox } from "./ApiTesterSandbox";

interface ViewRequestDialogProps {
  open: boolean;
  request: EndpointRequest;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<EndpointRequest>) => void;
  onDelete: (id: string) => void;
}

interface DevUser {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

const HTTP_METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];
const STATUSES: RequestStatus[] = ["pending", "in_progress", "completed", "blocked", "cancelled"];

export const ViewRequestDialog: React.FC<ViewRequestDialogProps> = ({
  open,
  request,
  onClose,
  onUpdate,
  onDelete,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const userIsBackendDev = isBackendDev(user);
  const userIsAdmin = isAdmin(user);
  const userCanClaim = userIsBackendDev || userIsAdmin;

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "sandbox" | "timeline">("details");
  const [openCodeSnippet, setOpenCodeSnippet] = useState(false);
  const [formData, setFormData] = useState<EndpointRequest>(request);
  const [developers, setDevelopers] = useState<DevUser[]>([]);
  const [loadingDevelopers, setLoadingDevelopers] = useState(false);
  const [assigningDev, setAssigningDev] = useState(false);

  // Completion Dialog State
  const [openCompletionModal, setOpenCompletionModal] = useState(false);
  const [completionEndpointUrl, setCompletionEndpointUrl] = useState("");
  const [completionStatusCode, setCompletionStatusCode] = useState<number>(
    request.method === "POST" ? 201 : 200
  );

  const [requestDate, setRequestDate] = useState<Dayjs | null>(
    request.requestDate ? dayjs(request.requestDate) : dayjs()
  );
  const [targetDate, setTargetDate] = useState<Dayjs | null>(
    request.targetDeliveryDate ? dayjs(request.targetDeliveryDate) : null
  );
  const [expectedDate, setExpectedDate] = useState<Dayjs | null>(
    request.expectedDeliveryDate ? dayjs(request.expectedDeliveryDate) : null
  );
  const [actualDate, setActualDate] = useState<Dayjs | null>(
    request.actualCompletionDate ? dayjs(request.actualCompletionDate) : null
  );

  React.useEffect(() => {
    if (open) {
      setFormData(request);
      setRequestDate(request.requestDate ? dayjs(request.requestDate) : dayjs());
      setTargetDate(request.targetDeliveryDate ? dayjs(request.targetDeliveryDate) : null);
      setExpectedDate(request.expectedDeliveryDate ? dayjs(request.expectedDeliveryDate) : null);
      setActualDate(request.actualCompletionDate ? dayjs(request.actualCompletionDate) : null);
      setIsEditing(false);
      setCompletionEndpointUrl(request.endpoint || "");
      setCompletionStatusCode(request.statusCode || (request.method === "POST" ? 201 : 200));
      fetchDevelopers();
    }
  }, [request, open]);

  const fetchDevelopers = async () => {
    setLoadingDevelopers(true);
    try {
      const response = await apiService.get<{ results?: DevUser[] } | DevUser[]>(
        "/api/authentication/users/?page_size=100"
      );
      const usersData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setDevelopers(usersData);
    } catch (error) {
      console.error("Failed to fetch developers:", error);
    } finally {
      setLoadingDevelopers(false);
    }
  };

  const handleAssignDeveloper = async (selection: DevUser | string | null) => {
    if (!request.id || !selection) return;
    setAssigningDev(true);
    try {
      const payload: Record<string, unknown> = {};
      if (typeof selection === "object" && selection.id) {
        payload.assigned_backend = selection.id;
        payload.assigned_backend_email = selection.email;
      } else if (typeof selection === "string" && selection.trim()) {
        payload.assigned_backend_email = selection.trim();
      }

      const response = await apiService.patch<{ assigned_backend?: DevUser; assigned_backend_email?: string }>(
        `/api/endpoint-request/endpoint-requests/${request.id}/assign/`,
        payload
      );
      const assignedUser: AssignedUser | null = response.data?.assigned_backend || (typeof selection === "object" ? selection : null);
      const emailStr = response.data?.assigned_backend_email || (typeof selection === "string" ? selection : selection.email);

      toast.success(`Request assigned to ${emailStr || "developer"} and email notification sent!`);
      setFormData((prev) => ({
        ...prev,
        assignedBackend: assignedUser,
        assignedBackendId: assignedUser?.id || null,
        assignedBackendEmail: emailStr,
      }));
      onUpdate(request.id, {
        assignedBackend: assignedUser,
        assignedBackendId: assignedUser?.id || null,
        assignedBackendEmail: emailStr,
      });
    } catch (error) {
      console.error("Failed to assign developer:", error);
      toast.error("Failed to assign developer. Please try again.");
    } finally {
      setAssigningDev(false);
    }
  };

  const handleSave = () => {
    if (!formData.component || !formData.task || !formData.lead) {
      toast.error("Please fill in all required fields");
      return;
    }

    const updates: Partial<EndpointRequest> = {
      ...formData,
      requestDate: requestDate?.format("YYYY-MM-DD") || formData.requestDate,
      targetDeliveryDate: targetDate?.format("YYYY-MM-DD") || undefined,
      expectedDeliveryDate: expectedDate?.format("YYYY-MM-DD") || undefined,
      actualCompletionDate: actualDate?.format("YYYY-MM-DD") || undefined,
    };

    onUpdate(request.id!, updates);
    setIsEditing(false);
    toast.success("Request updated successfully");
  };

  const handleQuickStatusChange = (newStatus: RequestStatus) => {
    if (newStatus === "completed") {
      if (!formData.endpoint || !formData.endpoint.trim()) {
        // Enforce endpoint entry before completing!
        setCompletionEndpointUrl(formData.endpoint || `/api/v1/${formData.component.toLowerCase().replace(/\s+/g, "-")}/`);
        setOpenCompletionModal(true);
        return;
      }
    }

    onUpdate(request.id!, { status: newStatus });
    setFormData((prev) => ({ ...prev, status: newStatus }));
    toast.success(`Status updated to ${newStatus.replace("_", " ")}`);
  };

  const handleConfirmCompletion = () => {
    if (!completionEndpointUrl || !completionEndpointUrl.trim()) {
      toast.error("Please enter the completed API Endpoint URL");
      return;
    }

    const todayStr = new Date().toISOString().split("T")[0];
    const updates: Partial<EndpointRequest> = {
      status: "completed",
      endpoint: completionEndpointUrl.trim(),
      statusCode: completionStatusCode,
      actualCompletionDate: todayStr,
      approvedBy: user?.username || "backend",
      approvedAt: new Date().toISOString(),
    };

    onUpdate(request.id!, updates);
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
    setOpenCompletionModal(false);
    toast.success(`Endpoint completed and published: ${completionEndpointUrl.trim()}`);
  };

  const handleClaimRequest = () => {
    if (!user?.username) return;
    onUpdate(request.id!, { status: "in_progress" });
    setFormData((prev) => ({ ...prev, status: "in_progress" }));
    toast.success(`Request claimed by ${user.username} and set to In Progress!`);
  };

  const handleCancel = () => {
    setFormData(request);
    setRequestDate(request.requestDate ? dayjs(request.requestDate) : dayjs());
    setTargetDate(request.targetDeliveryDate ? dayjs(request.targetDeliveryDate) : null);
    setExpectedDate(request.expectedDeliveryDate ? dayjs(request.expectedDeliveryDate) : null);
    setActualDate(request.actualCompletionDate ? dayjs(request.actualCompletionDate) : null);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this request?")) {
      onDelete(request.id!);
      toast.info("Request deleted");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getStatusProgress = (status: RequestStatus) => {
    switch (status) {
      case "completed":
        return 100;
      case "in_progress":
        return 60;
      case "pending":
        return 25;
      case "blocked":
      case "cancelled":
      default:
        return 0;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
            color: "white",
            p: 3,
            pb: 2.5,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  width: 48,
                  height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              >
                <ApiIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="800" sx={{ letterSpacing: "-0.5px" }}>
                  {formData.component}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Request #{formData.id} • Created by {formData.createdBy || formData.lead}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<CodeIcon />}
                onClick={() => setOpenCodeSnippet(true)}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.25)",
                  color: "white",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.35)" },
                }}
              >
                Code Generator
              </Button>

              {!isEditing ? (
                <>
                  <Tooltip title="Edit Request">
                    <IconButton
                      onClick={() => setIsEditing(true)}
                      size="small"
                      sx={{ color: "white", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete Request">
                    <IconButton
                      onClick={handleDelete}
                      size="small"
                      sx={{ color: "white", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <IconButton onClick={onClose} size="small" sx={{ color: "white" }}>
                    <CloseIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                    variant="contained"
                    size="small"
                    sx={{ backgroundColor: "white", color: primaryColor, fontWeight: 700 }}
                  >
                    Save
                  </Button>
                  <Button onClick={handleCancel} startIcon={<CancelIcon />} size="small" sx={{ color: "white" }}>
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* Progress Header & Quick Action Buttons */}
            <Box sx={{ px: 3.5, pt: 2.5, pb: 2, backgroundColor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="600">
                    Status:
                  </Typography>
                  <ModernStatusBadge status={formData.status} size="medium" />
                </Box>

                {/* Quick Status Action Buttons */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  {userCanClaim && formData.status === "pending" && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<ClaimIcon />}
                      onClick={handleClaimRequest}
                      sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
                    >
                      Claim & Start
                    </Button>
                  )}
                  {formData.status === "pending" && !userCanClaim && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PlayIcon />}
                      onClick={() => handleQuickStatusChange("in_progress")}
                      sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
                    >
                      Start Working
                    </Button>
                  )}
                  {formData.status !== "completed" && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleQuickStatusChange("completed")}
                      sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
                    >
                      Mark Completed
                    </Button>
                  )}
                  {formData.status !== "blocked" && formData.status !== "completed" && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<BlockIcon />}
                      onClick={() => handleQuickStatusChange("blocked")}
                      sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
                    >
                      Flag Blocked
                    </Button>
                  )}
                </Box>
              </Box>

              <LinearProgress
                variant="determinate"
                value={getStatusProgress(formData.status)}
                sx={{
                  height: 8,
                  borderRadius: "4px",
                  backgroundColor: "#E2E8F0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: formData.status === "completed" ? "#10B981" : primaryColor,
                  },
                }}
              />
            </Box>

            {/* Error Alert */}
            {request.statusCode && request.statusCode >= 400 && (
              <Alert severity="error" icon={<ErrorIcon />} sx={{ m: 3, mb: 0, borderRadius: "12px" }}>
                <Typography variant="subtitle2" fontWeight="700">
                  Error Status Code ({request.statusCode})
                </Typography>
                <Typography variant="body2">{request.challenges || "An issue was encountered"}</Typography>
              </Alert>
            )}

            {/* Navigation Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, mt: 1 }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                <Tab label="Details & Payload" value="details" sx={{ fontWeight: 700, textTransform: "none" }} />
                <Tab
                  label="API Sandbox & Mock Tester"
                  value="sandbox"
                  icon={<SandboxIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  sx={{ fontWeight: 700, textTransform: "none" }}
                />
                <Tab label="Audit & Timeline" value="timeline" sx={{ fontWeight: 700, textTransform: "none" }} />
              </Tabs>
            </Box>

            <Stack spacing={3} sx={{ p: 3.5 }}>
              {activeTab === "details" && (
                <>
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="700">
                          COMPONENT
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            value={formData.component}
                            onChange={(e) => setFormData({ ...formData, component: e.target.value })}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        ) : (
                          <Typography variant="h6" fontWeight="700" sx={{ mt: 0.5 }}>
                            {formData.component}
                          </Typography>
                        )}
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="700">
                          REQUESTED BY
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            value={formData.lead}
                            onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <Avatar sx={{ width: 30, height: 30, bgcolor: `${primaryColor}20`, color: primaryColor, fontWeight: 700 }}>
                              {formData.lead.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="h6" fontWeight="700">
                              {formData.lead}
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="700">
                            ASSIGNED BACKEND DEVELOPER
                          </Typography>
                          {assigningDev && <Typography variant="caption" color="primary.main">Sending notification...</Typography>}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                          {formData.assignedBackend ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 30, height: 30, fontSize: "0.85rem", bgcolor: "#E2E8F0", color: "#334155", fontWeight: 700 }}>
                                {(formData.assignedBackend.first_name || formData.assignedBackend.username || "D").charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ lineHeight: 1.2 }}>
                                  {formData.assignedBackend.first_name ? `${formData.assignedBackend.first_name} ${formData.assignedBackend.last_name || ""}` : formData.assignedBackend.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                  {formData.assignedBackend.email}
                                </Typography>
                              </Box>
                            </Box>
                          ) : formData.assignedBackendEmail ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 30, height: 30, fontSize: "0.85rem", bgcolor: "#E2E8F0", color: "#334155", fontWeight: 700 }}>
                                {formData.assignedBackendEmail.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ lineHeight: 1.2 }}>
                                  Developer
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                  {formData.assignedBackendEmail}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              Unassigned
                            </Typography>
                          )}

                          <Box sx={{ minWidth: 200, ml: "auto" }}>
                            <Autocomplete
                              freeSolo
                              size="small"
                              options={developers}
                              disabled={assigningDev || loadingDevelopers}
                              getOptionLabel={(option) => {
                                if (typeof option === "string") return option;
                                const nameStr = [option.first_name, option.last_name].filter(Boolean).join(" ");
                                return nameStr ? `${nameStr} (${option.email})` : option.email;
                              }}
                              onChange={(_, newValue) => {
                                if (newValue) handleAssignDeveloper(newValue);
                              }}
                              renderOption={(props, option) => {
                                const { key, ...optionProps } = props;
                                const nameStr = [option.first_name, option.last_name].filter(Boolean).join(" ");
                                return (
                                  <Box component="li" key={key} {...optionProps} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", py: 0.5 }}>
                                    <Typography variant="body2" fontWeight="600">
                                      {nameStr || option.username}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {option.email}
                                    </Typography>
                                  </Box>
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Assign or type email..."
                                  slotProps={{ input: { ...params.InputProps, sx: { borderRadius: "8px", fontSize: "0.8rem" } } }}
                                />
                              )}
                            />
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Task Description */}
                  <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="700">
                      TASK REQUIREMENT
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={formData.task}
                        onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-wrap", color: "text.primary" }}>
                        {formData.task}
                      </Typography>
                    )}
                  </Card>

                  {/* Endpoint & Method */}
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="700">
                            API ENDPOINT URL
                          </Typography>
                          {formData.endpoint && (
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(formData.endpoint || "")}
                              sx={{ color: "text.secondary" }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            value={formData.endpoint || ""}
                            onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
                            size="small"
                            sx={{ mt: 1 }}
                            placeholder="/api/v1/endpoint/"
                          />
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <CodeIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                            <Typography
                              variant="body1"
                              sx={{
                                fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                                fontWeight: 600,
                                color: formData.endpoint ? "primary.main" : "text.secondary",
                              }}
                            >
                              {formData.endpoint || "Not assigned yet"}
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="700">
                          HTTP METHOD
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value={formData.method || "GET"}
                              onChange={(e) => setFormData({ ...formData, method: e.target.value as HttpMethod })}
                            >
                              {HTTP_METHODS.map((m) => (
                                <MenuItem key={m} value={m}>
                                  {m}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Box sx={{ mt: 1 }}>
                            <ModernMethodChip method={formData.method} />
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Status, Priority, Dates */}
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="700">
                          STATUS
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value={formData.status}
                              onChange={(e) => setFormData({ ...formData, status: e.target.value as RequestStatus })}
                            >
                              {STATUSES.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status.replace("_", " ").toUpperCase()}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Box sx={{ mt: 1 }}>
                            <ModernStatusBadge status={formData.status} />
                          </Box>
                        )}
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="700">
                          PRIORITY
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value={formData.priority}
                              onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                            >
                              {PRIORITIES.map((p) => (
                                <MenuItem key={p} value={p}>
                                  {p.toUpperCase()}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Box sx={{ mt: 1 }}>
                            <ModernPriorityChip priority={formData.priority} />
                          </Box>
                        )}
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="700">
                          REQUEST DATE
                        </Typography>
                        {isEditing ? (
                          <DatePicker
                            value={requestDate}
                            onChange={(newValue) => setRequestDate(newValue as Dayjs | null)}
                            slotProps={{ textField: { size: "small", fullWidth: true, sx: { mt: 1 } } }}
                          />
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="600">
                              {formData.requestDate ? new Date(formData.requestDate).toLocaleDateString() : "N/A"}
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="700">
                          HTTP STATUS CODE
                        </Typography>
                        {isEditing ? (
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            value={formData.statusCode || ""}
                            onChange={(e) => setFormData({ ...formData, statusCode: parseInt(e.target.value, 10) || undefined })}
                            sx={{ mt: 1 }}
                          />
                        ) : (
                          <Typography variant="h6" fontWeight="700" sx={{ mt: 0.5 }}>
                            {formData.statusCode || "N/A"}
                          </Typography>
                        )}
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Dates Section */}
                  {isEditing && (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <DatePicker
                          label="Target Delivery Date"
                          value={targetDate}
                          onChange={(newValue) => setTargetDate(newValue as Dayjs | null)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <DatePicker
                          label="Expected Delivery Date"
                          value={expectedDate}
                          onChange={(newValue) => setExpectedDate(newValue as Dayjs | null)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <DatePicker
                          label="Actual Completion Date"
                          value={actualDate}
                          onChange={(newValue) => setActualDate(newValue as Dayjs | null)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {/* JSON Payload with JsonEditorInput */}
                  <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2.5 }}>
                    <JsonEditorInput
                      value={formData.payload || ""}
                      onChange={(val) => setFormData({ ...formData, payload: val })}
                      label="EXPECTED REQUEST PAYLOAD (JSON)"
                      readOnly={!isEditing}
                    />
                  </Card>

                  {/* Challenges / Issue notes */}
                  {(formData.challenges || isEditing) && (
                    <Card elevation={0} sx={{ borderRadius: "14px", border: "1px solid #E2E8F0", p: 2.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="700">
                        CHALLENGES / ISSUES
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          value={formData.challenges || ""}
                          onChange={(e) => setFormData({ ...formData, challenges: e.target.value })}
                          sx={{ mt: 1 }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                          {formData.challenges}
                        </Typography>
                      )}
                    </Card>
                  )}

                  {/* Attached Screenshot / Mockup */}
                  {formData.imageUrl && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom fontWeight="700">
                          ATTACHED SCREENSHOT / MOCKUP
                        </Typography>
                        <Box sx={{ mt: 1, textAlign: "center" }}>
                          <img
                            src={formData.imageUrl}
                            alt="Endpoint Screenshot Mockup"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "350px",
                              borderRadius: "12px",
                              objectFit: "contain",
                              border: "1px solid #e0e0e0",
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* TAB 1: API Sandbox */}
              {activeTab === "sandbox" && <ApiTesterSandbox request={formData} />}

              {/* TAB 2: Timeline Tab */}
              {activeTab === "timeline" && (
                <Box>
                  <Stack spacing={2}>
                    <Card elevation={0} sx={{ p: 2, borderRadius: "14px", border: "1px solid #E2E8F0" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar sx={{ bgcolor: `${primaryColor}20`, color: primaryColor }}>
                          <TimeIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="700">
                            Request Submitted
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(formData.requestDate).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>

                    {formData.targetDeliveryDate && (
                      <Card elevation={0} sx={{ p: 2, borderRadius: "14px", border: "1px solid #E2E8F0" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar sx={{ bgcolor: "#EFF6FF", color: "#3B82F6" }}>
                            <CalendarIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="700">
                              Target Delivery Date
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(formData.targetDeliveryDate).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    )}

                    {formData.actualCompletionDate && (
                      <Card elevation={0} sx={{ p: 2, borderRadius: "14px", border: "1px solid #E2E8F0" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar sx={{ bgcolor: "#ECFDF5", color: "#10B981" }}>
                            <CheckCircleIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="700">
                              Completed & Accomplished
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(formData.actualCompletionDate).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: "1px solid #E2E8F0" }}>
          <Button onClick={onClose} sx={{ borderRadius: "10px", color: "text.secondary", fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Code Snippet Modal */}
      <CodeSnippetDialog
        open={openCodeSnippet}
        onClose={() => setOpenCodeSnippet(false)}
        request={formData}
      />

      {/* Complete Request Modal (Enforce API Endpoint Entry) */}
      <Dialog
        open={openCompletionModal}
        onClose={() => setOpenCompletionModal(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: "18px" } } }}
      >
        <DialogTitle component="div" sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <ApproveIcon color="success" />
          <Typography variant="h6" fontWeight="800">
            Publish & Mark Endpoint Completed
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            To complete this endpoint request, please specify the final API endpoint URL so frontend developers can integrate with it.
          </Typography>

          <TextField
            fullWidth
            required
            autoFocus
            label="Completed API Endpoint URL *"
            placeholder="/api/v1/memory-table/"
            value={completionEndpointUrl}
            onChange={(e) => setCompletionEndpointUrl(e.target.value)}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "12px", fontFamily: "monospace" } }}
          />

          <FormControl fullWidth size="small" sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}>
            <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ mb: 0.5 }}>
              HTTP Status Code
            </Typography>
            <Select
              value={completionStatusCode}
              onChange={(e) => setCompletionStatusCode(Number(e.target.value))}
            >
              <MenuItem value={200}>200 OK</MenuItem>
              <MenuItem value={201}>201 Created</MenuItem>
              <MenuItem value={202}>202 Accepted</MenuItem>
              <MenuItem value={204}>204 No Content</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setOpenCompletionModal(false)} sx={{ borderRadius: "10px", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmCompletion}
            sx={{ borderRadius: "10px", fontWeight: 700, px: 3 }}
          >
            Confirm & Publish Endpoint
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
